/**
 * ErrorPage - Page d'erreur générique
 * Version moderne avec Vite + React 19 + Material-UI v7
 * Pour les erreurs de chargement, réseau, ou autres erreurs non-critiques
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  Box,
  Button,
  Container,
  Typography,
  Stack,
  Paper,
  Alert,
  Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Home as HomeIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

import Layout from '../components/Layout';

/**
 * Gestionnaire de rechargement de page
 * Fonction utilitaire pour recharger la page courante
 */
const handlePageReload = () => {
  globalThis.location.reload();
};

/**
 * ErrorPage Component
 * Page d'erreur générique avec informations sur l'erreur et options de récupération
 * @param {Object} props - Props du composant
 * @param {Error} props.error - Objet erreur (optionnel)
 * @param {string} props.title - Titre personnalisé de l'erreur
 * @param {string} props.message - Message personnalisé d'erreur
 * @param {boolean} props.showReload - Afficher le bouton de rechargement
 * @param {boolean} props.showBack - Afficher le bouton retour
 * @returns {JSX.Element} Page d'erreur avec options de navigation
 */
const ErrorPage = ({
  error = null,
  title = "Une erreur s'est produite",
  message = "Nous nous excusons pour ce problème. Veuillez réessayer.",
  showReload = true,
  showBack = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Extract error information from navigation state if available
  const routerError = location.state?.error;
  const displayError = error || routerError;

  /**
   * Gestionnaire de navigation vers l'accueil
   */
  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  /**
   * Gestionnaire de retour en arrière
   */
  const handleGoBack = () => {
    if (globalThis.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  };

  /**
   * Déterminer le type d'erreur pour l'affichage
   * @returns {string} Type d'erreur détecté
   */
  const getErrorType = () => {
    if (!displayError) return 'unknown';

    const errorMessage = displayError.message?.toLowerCase() || '';

    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return 'network';
    }
    if (errorMessage.includes('not found') || errorMessage.includes('404')) {
      return 'notfound';
    }
    if (errorMessage.includes('timeout')) {
      return 'timeout';
    }
    return 'generic';
  };

  const errorType = getErrorType();

  /**
   * Messages d'erreur personnalisés selon le type
   * @returns {Object} Objet contenant title, message et suggestion
   */
  const getErrorMessages = () => {
    switch (errorType) {
      case 'network': {
        return {
          title: 'Problème de connexion',
          message: 'Impossible de charger les données. Vérifiez votre connexion internet.',
          suggestion: 'Réessayez dans quelques instants ou rechargez la page.',
        };
      }
      case 'timeout': {
        return {
          title: 'Délai d\'attente dépassé',
          message: 'Le chargement prend plus de temps que prévu.',
          suggestion: 'Le serveur met du temps à répondre. Veuillez patienter et réessayer.',
        };
      }
      case 'notfound': {
        return {
          title: 'Ressource non trouvée',
          message: 'La ressource demandée n\'existe pas ou n\'est plus disponible.',
          suggestion: 'Vérifiez l\'URL ou retournez à l\'accueil.',
        };
      }
      default: {
        return {
          title,
          message,
          suggestion: 'Si le problème persiste, contactez le support technique.',
        };
      }
    }
  };

  const errorMessages = getErrorMessages();

  return (
    <Layout
      title={`Erreur - ${errorMessages.title}`}
      maxWidth="md"
    >
      <Container
        maxWidth="sm"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          py: 4,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: 4,
            borderRadius: 3,
            width: '100%',
            background: 'linear-gradient(145deg, #fff8e1 0%, #ffffff 100%)',
            border: '1px solid #ffcc02',
          }}
        >
          {/* Icône et titre */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <WarningIcon sx={{ fontSize: '3rem', color: 'warning.main' }} />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: 'warning.main',
                  mb: 1,
                }}
              >
                {errorMessages.title}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Code d'erreur : {displayError?.status || 'UNKNOWN'}
              </Typography>
            </Box>
          </Stack>

          {/* Message principal */}
          <Typography
            variant="body1"
            sx={{
              color: 'text.primary',
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            {errorMessages.message}
          </Typography>

          {/* Suggestion */}
          <Alert severity="info" sx={{ mb: 4 }}>
            {errorMessages.suggestion}
          </Alert>

          {/* Boutons d'action */}
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              {showReload && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RefreshIcon />}
                  onClick={handlePageReload}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                  }}
                >
                  Recharger
                </Button>
              )}

              <Button
                variant="outlined"
                size="large"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                }}
              >
                Accueil
              </Button>
            </Stack>

            {showBack && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    ou
                  </Typography>
                </Divider>

                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleGoBack}
                  sx={{
                    alignSelf: 'center',
                    textTransform: 'none',
                  }}
                >
                  Retour à la page précédente
                </Button>
              </>
            )}
          </Stack>

          {/* Détails techniques (développement) */}
          {import.meta.env.DEV && displayError && (
            <Box
              sx={{
                mt: 4,
                p: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1 }}
              >
                Détails de l'erreur (développement) :
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  color: 'error.main',
                }}
              >
                {displayError.message || displayError.toString()}
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Layout>
  );
};

export default ErrorPage;
